// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — Theme Context
// Manages dark/light theme switching with localStorage persistence
// ══════════════════════════════════════════════════════════════════════════════

import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { getTheme } from '@constants/theme';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [themeName, setThemeName] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    return localStorage.getItem('seldor-theme') || 'dark';
  });

  useEffect(() => {
    localStorage.setItem('seldor-theme', themeName);
    document.body.style.backgroundColor = getTheme(themeName).bg;
  }, [themeName]);

  const toggleTheme = () => setThemeName(prev => (prev === 'dark' ? 'light' : 'dark'));

  const value = useMemo(() => ({
    theme: getTheme(themeName),
    themeName,
    setThemeName,
    toggleTheme
  }), [themeName]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
};
